/**
 * DynamicKanbanBoard Component
 *
 * Board Kanban dinamica (Planner-style): colonne Gamma/Personalizzati liberamente
 * gestibili, drag & drop dei task tra colonne (o riordino nella stessa colonna),
 * completamento/riapertura rapidi dalla card. I dati della board sono di proprietà della
 * pagina chiamante (KanbanPage), che li passa qui come prop: evita una doppia query
 * identica (la pagina ne ha comunque bisogno per i metadata di BoardFiltersBar).
 */
import { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Group, Loader, Modal, Paper, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPlus } from '@tabler/icons-react';

import type {
  DynamicKanbanBoard as DynamicKanbanBoardType,
  DynamicKanbanColumnData,
  KanbanColumnDefinition,
  ProjectTaskSummary,
} from '../../types/api';
import {
  kanbanColumnKeys,
  useCreateKanbanColumn,
  useDeleteKanbanColumn,
  useKanbanColumns,
  useMoveTaskToColumn,
  useUpdateKanbanColumn,
} from '../../hooks/use-kanban-columns';
import { useCompleteKanbanTask, useReopenKanbanTask } from '../../hooks/use-kanban-tasks';
import { useDeleteTask } from '../../hooks/use-tasks';
import { isTaskCompleted } from '../../utils/task-status-utils';
import { boardCollisionDetection, findTaskColumn, parseTaskDndId, splitColumnTasks } from './board-utils';
import DynamicKanbanColumn from './DynamicKanbanColumn';
import TaskCardView from './TaskCardView';
import ColumnManagementModal from './ColumnManagementModal';
import DeadlineAlertsPanel from './DeadlineAlertsPanel';

interface DynamicKanbanBoardProps {
  board?: DynamicKanbanBoardType;
  isLoading: boolean;
  isError: boolean;
  showCovers: boolean;
  isCompletedExpanded: (columnId: number) => boolean;
  onToggleCompleted: (columnId: number) => void;
  onTaskClick: (taskId: number) => void;
  onAddTask: (columnId?: number) => void;
  height?: string;
  showAlerts?: boolean;
}

export default function DynamicKanbanBoard({
  board,
  isLoading,
  isError,
  showCovers,
  isCompletedExpanded,
  onToggleCompleted,
  onTaskClick,
  onAddTask,
  height = '70vh',
  showAlerts = true,
}: DynamicKanbanBoardProps) {
  const queryClient = useQueryClient();
  const { data: allColumns } = useKanbanColumns();

  const moveTask = useMoveTaskToColumn();
  const completeTask = useCompleteKanbanTask();
  const reopenTask = useReopenKanbanTask();
  const createColumn = useCreateKanbanColumn();
  const updateColumn = useUpdateKanbanColumn();
  const deleteColumn = useDeleteKanbanColumn();
  const deleteTask = useDeleteTask();

  const [activeTask, setActiveTask] = useState<ProjectTaskSummary | null>(null);
  const [columnModalOpened, setColumnModalOpened] = useState(false);
  const [editingColumn, setEditingColumn] = useState<KanbanColumnDefinition | null>(null);
  const [deleteConfirmDefault, setDeleteConfirmDefault] = useState(false);
  const [deleteTaskTarget, setDeleteTaskTarget] = useState<ProjectTaskSummary | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleToggleTaskComplete = (task: ProjectTaskSummary) => {
    if (isTaskCompleted(task)) {
      reopenTask.mutate(task.id);
    } else {
      completeTask.mutate({ id: task.id });
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    if (!board) return;
    const taskId = parseTaskDndId(event.active.id);
    if (taskId == null) return;
    const column = findTaskColumn(board, taskId);
    const task = column?.tasks.find((t) => t.id === taskId);
    setActiveTask(task ?? null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveTask(null);
    if (!board) return;

    const { active, over } = event;
    if (!over) return;

    const taskId = parseTaskDndId(active.id);
    if (taskId == null) return;

    const sourceColumn = findTaskColumn(board, taskId);
    if (!sourceColumn) return;

    const overData = over.data.current as { type?: string; columnId?: number; taskId?: number } | undefined;
    if (!overData?.type || overData.columnId == null) return;

    const targetColumnId = overData.columnId;
    let beforeTaskId: number | null = null;

    if (overData.type === 'column') {
      beforeTaskId = null;
    } else if (overData.type === 'task') {
      const overTaskId = overData.taskId;
      if (overTaskId == null) return;

      const targetColumn = board.columns.find((c) => c.id === targetColumnId);
      if (!targetColumn) return;
      const activeIds = splitColumnTasks(targetColumn).active.map((t) => t.id);
      const sameColumn = sourceColumn.id === targetColumnId;

      if (sameColumn) {
        const oldIndex = activeIds.indexOf(taskId);
        const newIndex = activeIds.indexOf(overTaskId);
        if (oldIndex === -1 || newIndex === -1) return;
        const reordered = arrayMove(activeIds, oldIndex, newIndex);
        const idxAfter = reordered.indexOf(taskId) + 1;
        beforeTaskId = idxAfter < reordered.length ? reordered[idxAfter] : null;
      } else {
        beforeTaskId = overTaskId;
      }
    } else {
      return;
    }

    // Evita una mutazione inutile se il task resta nella stessa colonna, nella stessa posizione.
    if (sourceColumn.id === targetColumnId) {
      const currentIds = splitColumnTasks(sourceColumn).active.map((t) => t.id);
      const currentIdx = currentIds.indexOf(taskId);
      const currentNext = currentIdx >= 0 && currentIdx + 1 < currentIds.length ? currentIds[currentIdx + 1] : null;
      if (currentNext === beforeTaskId) return;
    }

    moveTask.mutate({ taskId, data: { columnId: targetColumnId, beforeTaskId } });
  };

  const openCreateColumnModal = () => {
    setEditingColumn(null);
    setDeleteConfirmDefault(false);
    setColumnModalOpened(true);
  };

  const openEditColumnModal = (column: DynamicKanbanColumnData) => {
    setEditingColumn(allColumns?.find((c) => c.id === column.id) ?? null);
    setDeleteConfirmDefault(false);
    setColumnModalOpened(true);
  };

  const openDeleteColumnConfirm = (column: DynamicKanbanColumnData) => {
    setEditingColumn(allColumns?.find((c) => c.id === column.id) ?? null);
    setDeleteConfirmDefault(true);
    setColumnModalOpened(true);
  };

  const closeColumnModal = () => {
    setColumnModalOpened(false);
    setEditingColumn(null);
    setDeleteConfirmDefault(false);
  };

  const handleSaveColumn = async (data: any) => {
    try {
      if (editingColumn) {
        await updateColumn.mutateAsync({ id: editingColumn.id, data });
      } else {
        await createColumn.mutateAsync(data);
      }
      closeColumnModal();
    } catch {
      // Errore già notificato dal hook.
    }
  };

  const handleDeleteColumnConfirm = async (id: number, moveTasksToColumnId?: number) => {
    try {
      await deleteColumn.mutateAsync({ id, moveTasksToColumnId });
      closeColumnModal();
    } catch {
      // Errore già notificato dal hook.
    }
  };

  const confirmDeleteTask = (task: ProjectTaskSummary) => setDeleteTaskTarget(task);

  const handleDeleteTaskConfirm = async () => {
    if (!deleteTaskTarget) return;
    try {
      await deleteTask.mutateAsync(String(deleteTaskTarget.id));
      queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.boards() });
      notifications.show({ title: 'Task eliminato', message: 'Il task è stato eliminato', color: 'green' });
    } catch {
      notifications.show({ title: 'Errore', message: 'Impossibile eliminare il task', color: 'red' });
    } finally {
      setDeleteTaskTarget(null);
    }
  };

  if (isLoading) {
    return (
      <Group justify="center" py="xl" style={{ height }}>
        <Loader size="lg" />
      </Group>
    );
  }

  if (isError) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={18} />} title="Errore">
        Impossibile caricare la board.
      </Alert>
    );
  }

  if (!board) {
    return null;
  }

  return (
    <Box>
      {showAlerts && <DeadlineAlertsPanel onTaskClick={onTaskClick} />}

      {board.columns.length === 0 ? (
        <Paper p="xl" withBorder>
          <Stack align="center" gap="sm">
            <Text c="dimmed">Nessuna colonna configurata.</Text>
            <Button leftSection={<IconPlus size={16} />} onClick={openCreateColumnModal}>
              Crea la prima colonna
            </Button>
          </Stack>
        </Paper>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={boardCollisionDetection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Box style={{ overflowX: 'auto', paddingBottom: 8 }}>
            <Group align="stretch" gap="md" wrap="nowrap" style={{ height, minWidth: 'max-content' }}>
              {board.columns.map((column) => (
                <DynamicKanbanColumn
                  key={column.id}
                  column={column}
                  showCovers={showCovers}
                  isCompletedExpanded={isCompletedExpanded(column.id)}
                  onToggleCompleted={() => onToggleCompleted(column.id)}
                  onTaskClick={onTaskClick}
                  onToggleTaskComplete={handleToggleTaskComplete}
                  onDeleteTask={confirmDeleteTask}
                  onAddTask={onAddTask}
                  onEditColumn={openEditColumnModal}
                  onDeleteColumn={openDeleteColumnConfirm}
                />
              ))}

              <Paper
                withBorder
                p="md"
                onClick={openCreateColumnModal}
                style={{
                  minWidth: 220,
                  height: 'fit-content',
                  cursor: 'pointer',
                  border: '2px dashed var(--mantine-color-gray-4)',
                  backgroundColor: 'var(--mantine-color-gray-0)',
                }}
              >
                <Group justify="center" gap="xs">
                  <IconPlus size={18} color="#868e96" />
                  <Text c="dimmed">Aggiungi colonna</Text>
                </Group>
              </Paper>
            </Group>
          </Box>

          <DragOverlay dropAnimation={null}>
            {activeTask && <TaskCardView task={activeTask} showCover={showCovers} overlay />}
          </DragOverlay>
        </DndContext>
      )}

      <ColumnManagementModal
        opened={columnModalOpened}
        onClose={closeColumnModal}
        column={editingColumn}
        onSave={handleSaveColumn}
        onDelete={handleDeleteColumnConfirm}
        isLoading={createColumn.isPending || updateColumn.isPending || deleteColumn.isPending}
        initialDeleteConfirm={deleteConfirmDefault}
      />

      <Modal opened={deleteTaskTarget !== null} onClose={() => setDeleteTaskTarget(null)} title="Conferma eliminazione" centered>
        <Stack>
          <Text>
            Sei sicuro di voler eliminare il task "{deleteTaskTarget?.title}"? Questa azione non può essere annullata.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeleteTaskTarget(null)}>
              Annulla
            </Button>
            <Button color="red" onClick={handleDeleteTaskConfirm} loading={deleteTask.isPending}>
              Elimina
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
